/**
 * ONE reading of "is this vendor open?" for every list, card and badge.
 * ─────────────────────────────────────────────────────────────────────────────
 * There is deliberately NO clock arithmetic in this file, and there must never
 * be any. The server publishes `openStatus` on every vendor row, computed by
 * the same resolver that answers the storefront's /open-status endpoint, so a
 * card and the shop it opens cannot disagree.
 *
 * What this replaces, and why it had to go:
 *
 *   - The brands list rendered its pill from `isActive`, the admin on/off
 *     switch, so every enabled brand read "Open" around the clock — including
 *     at 3am, and including brands whose storefront was simultaneously saying
 *     "Closed right now".
 *   - The pharmacy list ran a second, hand-written copy of the hours maths
 *     (`utility/hoursUtils`) that could not represent a 24/7 vendor (00:00
 *     to 00:00 evaluated as `current >= 0 && current < 0`) or an overnight slot
 *     (18:00 to 02:00 can never satisfy `current >= open && current < close`),
 *     and fell back to UTC — three hours off Kampala. Those vendors read
 *     CLOSED in the list while their storefront read open.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: an unknown state renders NOTHING. Not
 * "Open". A missing field is not evidence that a shop is trading.
 */

import {
  ORDER_AHEAD_LABEL,
  ORDERING_MODE,
  resolveOrderingMode,
} from "./deliverySchedule";

/**
 * `isOpen` is deliberately three-valued:
 *   true  — inside published hours
 *   false — outside published hours
 *   null  — nothing published, or this build is talking to an older server
 */
const legacyIsOpen = (vendor) => {
  if (typeof vendor?.currentlyOpen === "boolean") return vendor.currentlyOpen;
  if (typeof vendor?.isOpen === "boolean") return vendor.isOpen;
  return null;
};

/**
 * @param vendor    a vendor row from any list endpoint
 * @param override  a fresher status for the same vendor — a socket push, or a
 *                  storefront's own availability payload
 */
export const readVendorOpenState = (vendor, override = null) => {
  const status = override || vendor?.openStatus || null;
  const isActive =
    vendor?.isActive === false ? false : status?.isActive !== false;

  let isOpen;
  if (status && typeof status.isOpen === "boolean") isOpen = status.isOpen;
  else if (status && status.isOpen === null) isOpen = null;
  else isOpen = legacyIsOpen(vendor);

  return {
    isActive,
    isOpen,
    // `false` means the vendor has published no hours. The server keeps those
    // vendors trading on purpose (code HOURS_NOT_CONFIGURED) — do not draw a
    // closed badge for them, and do not block ordering.
    scheduleConfigured: status
      ? status.scheduleConfigured !== false
      : isOpen !== null,
    // Per-vertical policy: brands, shops and pharmacies that have published
    // nothing present as "Open 24/7" rather than as a blank. Restaurants do
    // not — an order placed at 3am against unpublished hours is a promise
    // nobody made. This is a display flag; `scheduleConfigured` above is still
    // the honest answer to "has this vendor published hours?".
    presentsAsAlwaysOpen: status?.presentsAsAlwaysOpen === true,
    // Closed now, takes scheduled orders (shared decision in deliverySchedule).
    scheduledOrdersOnly:
      resolveOrderingMode({ isActive, isOpen, scheduledOrdersOnly: status?.scheduledOrdersOnly }) ===
      ORDERING_MODE.SCHEDULED_ONLY,
    code: status?.code || null,
    message: status?.message || null,
    nextOpening: status?.nextOpening || null,
    todayHours: status?.todayHours || null,
    timezone: status?.timezone || null,
  };
};

/** Badge tones, so every surface in the app colours these the same way. */
export const OPEN_BADGE_TONES = {
  open: { background: "rgba(16,185,129,0.85)", solid: "#10B981", text: "#FFFFFF" },
  // Closed now, taking orders for later. Calm blue, never the red of "Closed":
  // the shopper can still browse, fill a cart and pick a delivery time.
  scheduled: { background: "rgba(29,78,216,0.85)", solid: "#1D4ED8", text: "#FFFFFF" },
  closed: { background: "rgba(239,68,68,0.85)", solid: "#EF4444", text: "#FFFFFF" },
  unavailable: { background: "rgba(107,114,128,0.85)", solid: "#6B7280", text: "#FFFFFF" },
};

/**
 * The badge to draw, or `null` for "draw nothing".
 *
 * Null is a real answer and callers must honour it. Rendering "Open" for an
 * unknown state is the exact bug this module was written to end.
 */
export const vendorOpenBadge = (vendor, override = null) => {
  const state = readVendorOpenState(vendor, override);
  if (!state.isActive) {
    return { tone: "unavailable", label: "Unavailable", state };
  }
  if (state.isOpen === true) {
    return {
      tone: "open",
      // Say which kind of open it is. "Open" against published hours is a
      // different claim from "we take orders whenever", and collapsing them
      // is how a customer ends up surprised.
      label: state.scheduleConfigured ? "Open" : "Open 24/7",
      state,
    };
  }
  // Closed, but this vendor takes orders for later: say what the shopper can
  // do, not what they cannot.
  if (state.scheduledOrdersOnly) {
    return { tone: "scheduled", label: ORDER_AHEAD_LABEL, state };
  }
  if (state.isOpen === false) return { tone: "closed", label: "Closed", state };
  return null;
};

/**
 * Open right now? true, false, or null when the vendor publishes nothing we
 * can read. For choosing which of a chain's locations a card opens on
 * (utils/vendorOutlets.onePerVendor, 4 Oct 2026): "taking orders for later",
 * closed and unavailable all count as not open now, so an open branch is
 * preferred to them. The same reading the mobile apps make.
 */
export const openNowOf = (vendor, override = null) => {
  const badge = vendorOpenBadge(vendor, override);
  return badge ? badge.tone === "open" : null;
};

/**
 * Should the customer be stopped from ordering?
 *
 * Only a vendor we KNOW is shut. Unknown never blocks — the server accepts
 * those orders, so blocking them here would shutter vendors the platform has
 * deliberately kept trading.
 */
export const vendorBlocksOrdering = (vendor, override = null) => {
  const state = readVendorOpenState(vendor, override);
  if (!state.isActive) return true;
  // Closed now but taking orders for later is not a block: the shopper browses,
  // fills a cart and picks a delivery time at checkout.
  if (state.scheduledOrdersOnly) return false;
  return state.isOpen === false;
};

/* ── Formatters ──────────────────────────────────────────────────────────────
 * Pure presentation over values the SERVER computed. They read `todayHours`
 * and `nextOpening` off the payload and never ask what time it is.
 */

const to12Hour = (value) => {
  if (typeof value !== "string" || !/^\d{1,2}:\d{2}$/.test(value)) return value;
  const [hours, minutes] = value.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const display = hours % 12 || 12;
  return `${display}:${String(minutes).padStart(2, "0")} ${period}`;
};

const isFullDaySlot = (slot) =>
  slot?.openTime === slot?.closeTime ||
  (slot?.openTime === "00:00" && slot?.closeTime === "23:59");

/** `todayHours` is the server's `{ isClosed, slots, isSpecial, reason }`. */
export const formatTodayHours = (todayHours) => {
  if (!todayHours) return null;
  if (todayHours.isClosed) {
    return todayHours.reason ? `Closed today — ${todayHours.reason}` : "Closed today";
  }
  const slots = Array.isArray(todayHours.slots) ? todayHours.slots : [];
  if (slots.length === 0) return null;
  if (slots.some(isFullDaySlot)) return "Open 24 hours";
  return slots
    .map((slot) => `${to12Hour(slot.openTime)} - ${to12Hour(slot.closeTime)}`)
    .join(", ");
};

/** `nextOpening` is the server's `{ day, time, isToday, isTomorrow }`. */
export const formatNextOpening = (nextOpening) => {
  if (!nextOpening?.time) return null;
  const when = nextOpening.isToday
    ? "today"
    : nextOpening.isTomorrow
      ? "tomorrow"
      : nextOpening.day;
  return `Opens ${when} at ${to12Hour(nextOpening.time)}`;
};

/**
 * The hours line to show under a badge.
 *
 * `formatTodayHours` alone returns null for an always-open vendor, because
 * there is no published window to format — which left the pill rendering an
 * empty string next to "Open 24/7".
 */
export const describeHours = (state) => {
  if (!state) return null;
  if (!state.scheduleConfigured) {
    return state.presentsAsAlwaysOpen ? "Open 24 hours" : null;
  }
  return formatTodayHours(state.todayHours);
};

// Named before export: CRA's `import/no-anonymous-default-export` is a warning
// everywhere else and a BUILD FAILURE on Netlify, which sets CI=true.
const vendorOpenState = {
  readVendorOpenState,
  describeHours,
  vendorOpenBadge,
  openNowOf,
  vendorBlocksOrdering,
  formatTodayHours,
  formatNextOpening,
  OPEN_BADGE_TONES,
};

export default vendorOpenState;
